import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
// Note: Using React 19's native document metadata support instead of react-helmet-async
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.jsx'
import { AuthProvider } from './auth/AuthProvider'
import { UserProvider } from './auth/UserContext'
import { PaddleProvider } from './paddle/PaddleContext'
import { ThemeProvider } from './theme/ThemeContext'

// Initialize Sentry for error tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring - capture 100% in dev, reduce in prod
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out expected errors - only send unexpected ones
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // Ignore expected business logic errors
      if (error?.name) {
        const ignoredErrors = [
          'LimitExceededError',
          'SignupRequiredError',
          'InsufficientCreditsError',
          'EmailNotVerifiedError',
        ];
        if (ignoredErrors.includes(error.name)) {
          return null; // Drop the event
        }
      }

      // Ignore fetch/HTTP errors for expected status codes
      if (error?.message) {
        const msg = error.message.toLowerCase();
        // Ignore auth errors (401), forbidden (403), rate limits (429)
        if (msg.includes('401') || msg.includes('unauthorized') ||
          msg.includes('403') || msg.includes('forbidden') ||
          msg.includes('429') || msg.includes('rate limit') ||
          msg.includes('limit exceeded') ||
          msg.includes('signup required')) {
          return null;
        }
      }

      return event;
    },
  })
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <BrowserRouter>
      <Sentry.ErrorBoundary fallback={<div className="p-8 text-center">Something went wrong. Please refresh the page.</div>}>
        <AuthProvider>
          <UserProvider>
            <PaddleProvider>
              <ThemeProvider>
                <App />
              </ThemeProvider>
            </PaddleProvider>
          </UserProvider>
        </AuthProvider>
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  </StrictMode>,
)
